import * as React from 'react'
import { cn } from '@/lib/utils'
import { Icon3D } from '@/components/icons3d/Icon3D'
import { LUCIDE_TO_3D } from '@/components/icons3d/icons3d'
import { useSkin } from '@/theme/skin'

/**
 * The "nothing here yet" panel: one icon, one line of what is missing, one
 * line of what to do, and at most one action.
 *
 * @param {Object} props
 * @param {React.ElementType|React.ReactElement} [props.icon] - A lucide
 *   component (rendered at size-5) or a ready-made element
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.message]
 * @param {React.ReactNode} [props.action]
 * @param {'md'|'sm'} [props.size='md'] - `sm` for inline use in cards and pickers
 * @param {string|null} [props.role='status'] - The panel announces itself to
 *   assistive tech by default; pass `null` when the caller already owns a
 *   more precise live region for the same result (e.g. a search page's own
 *   "N results found" `aria-live` count) so the two don't both announce and
 *   don't collide as duplicate `role="status"` landmarks.
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function EmptyState({ icon, title, message, action, size = 'md', role = 'status', className }) {
  // lucide icons are forwardRef objects, not functions, so `typeof` cannot
  // tell a component from a ready-made element — ask React instead.
  const Icon = icon && !React.isValidElement(icon) ? icon : null
  const skin = useSkin()
  const name3d = skin === 'fluent3d' ? (Icon && LUCIDE_TO_3D.get(Icon)) || (Icon?.displayName?.match(/^Icon3D\((.+)\)$/)?.[1]) || null : null

  return (
    <div
      role={role ?? undefined}
      className={cn(
        'flex flex-col items-center justify-center px-4 text-center',
        size === 'sm' ? 'py-8' : 'py-12',
        className
      )}
    >
      {name3d ? (
        <Icon3D name={name3d} size={size === 'sm' ? 64 : 96} className="mb-3" />
      ) : icon && (
        <div className="mb-4 flex size-10 items-center justify-center rounded-full bg-surface-2 text-ink-2">
          {Icon ? <Icon className="size-5" /> : icon}
        </div>
      )}
      <h3 className="text-body-lg font-medium text-ink">{title}</h3>
      {message && <p className="mt-1 max-w-md text-body text-ink-2">{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export default EmptyState
